import { logError } from '@ptek/client-logger';
import { apiRouter } from '../config/api';

export interface UpdateDocumentRequest {
  content?: string;
  status?: 'pending' | 'approved' | 'needs_revision';
  title?: string;
  metadata?: Record<string, unknown>;
  generatedContent?: Record<string, unknown>;
}

export interface UpdateDocumentResponse {
  success: boolean;
  data?: {
    documentId: string;
    updatedFields: string[];
    updatedAt: string;
  };
  message: string;
  metadata?: {
    updatedBy: string;
    userContext: {
      market: string;
      brand: string;
      customerId: string;
    };
  };
}

export class DocumentsClient {
  /**
   * Make API call using the router that automatically directs to legacy or new service
   */
  private async makeApiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<unknown> {
    return apiRouter.makeApiCall(endpoint, options);
  }

  /**
   * Update a document
   * PUT /v1/documents/{id}
   */
  async updateDocument(
    documentId: string,
    updates: UpdateDocumentRequest
  ): Promise<UpdateDocumentResponse> {
    try {
      const data = (await this.makeApiCall(`/api/v1/documents/${documentId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })) as UpdateDocumentResponse;

      return data;
    } catch (error) {
      logError('DocumentsClient', 'Failed to update document:', {
        documentId,
        updates,
        error: error instanceof Error ? error.message : String(error),
      });

      if (error instanceof Error) {
        if (error.message.includes('404')) {
          throw new Error(
            `Document not found or access denied (ID: ${documentId})`
          );
        } else if (error.message.includes('400')) {
          throw new Error('Invalid document update request');
        } else if (error.message.includes('403')) {
          throw new Error(
            'Permission denied - you may not have access to this document'
          );
        } else if (error.message.includes('500')) {
          throw new Error('Server error - please try again later');
        }
      }

      throw error;
    }
  }
}

export const documentsClient = new DocumentsClient();
